import {
  Alert,
  AlertTitle,
  Box,
  Divider,
  Grid,
  Pagination,
  Skeleton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import {
  searchDatasetFolders,
  searchDatasets,
} from "../services/DatasetServices";

import { useParams } from "react-router-dom";
import { Can } from "src/helpers/Auth";
import { extractErrorDetail } from "src/helpers/apiError";
import { NoDataOverlay } from "src/reusable/components/NoDataOverlay";
import { whereFilterType } from "src/types/types";
import { CreateDatasetModal } from "src/views/forms/datasetModels/CreateDatasetModal";
import { CreateItemButton } from "src/views/forms/FormUtils";
import DatasetCard from "./DatasetCard";
import { QueryOptions } from "./DatasetCardSearch";

interface DatasetCardsGridProps {
  queryOptions: QueryOptions;
  debouncedSearchQuery: string;
  whereFilters: whereFilterType[];
  atomResults?: boolean;
  showFavoritesOnly?: boolean;
}

const DatasetCardsGrid = ({
  queryOptions,
  debouncedSearchQuery,
  whereFilters,
  atomResults = false,
  showFavoritesOnly = false,
}: DatasetCardsGridProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const queryClient = useQueryClient();
  const { folder_id }: any = useParams();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(12); // Fixed page size for cards view
  const [sortBy] = useState("create_date");
  const [sortDesc] = useState(true);

  // add the folder_id to the queryOptions as own property
  queryOptions.folderId = folder_id;

  // Only expand search scope to folder-nested datasets when the user is
  // actively filtering at the root level. Idle root view stays scoped to
  // root datasets so folder contents don't appear twice (issue #1727).
  const searchAllFolders =
    !folder_id &&
    (debouncedSearchQuery.trim().length > 0 ||
      showFavoritesOnly ||
      whereFilters.length > 0);

  const { data, isLoading, isFetching, isSuccess, isError, error } = useQuery({
    queryKey: [
      "datasetList",
      debouncedSearchQuery,
      whereFilters,
      currentPage,
      pageSize,
      sortBy,
      sortDesc,
      folder_id, // Add folder_id to prevent cache collisions
      atomResults,
      showFavoritesOnly, // Add to query key to refetch when filter changes
      searchAllFolders,
    ],
    queryFn: () =>
      searchDatasets({
        ...(queryOptions as any),
        page: currentPage,
        pageSize: pageSize,
        sortBy: sortBy,
        sortDesc: sortDesc,
        starredOnly: showFavoritesOnly,
        searchAllFolders,
      }),
    keepPreviousData: !atomResults,
    staleTime: 10 * 1000, // 10 seconds for search results
    cacheTime: 2 * 60 * 1000, // 2 minutes cache
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    retry: (failureCount, error: any) => {
      // Don't retry on 4xx errors but retry on network issues
      if (error?.response?.status >= 400 && error?.response?.status < 500) {
        return false;
      }
      return failureCount < 3;
    },
    refetchOnReconnect: true,
  });
  const {
    data: datasetFolderData,
    isSuccess: isDatasetFolderDataSuccess,
    isLoading: isDatasetFolderLoading,
    isFetching: isDatasetFolderFetching,
  } = useQuery({
    queryKey: ["datasetFolders", queryOptions.searchQuery],
    queryFn: () =>
      searchDatasetFolders({
        searchQuery: queryOptions.searchQuery,
        page: 1,
        pageSize: 50,
        sortBy: "create_date",
        sortDesc: true,
      }),
    enabled: !Boolean(folder_id),
    staleTime: 30 * 1000, // 30 seconds stale time for folders
    cacheTime: 5 * 60 * 1000, // 5 minutes cache
    refetchOnWindowFocus: false,
    retry: 2,
    refetchOnReconnect: true,
  });

  useEffect(() => {
    if (debouncedSearchQuery !== "" || whereFilters.length > 0) {
      setCurrentPage(1); // Reset to first page when search/filters change
      queryClient.invalidateQueries(["datasets"]);
    }
  }, [debouncedSearchQuery, whereFilters, queryClient]);

  // Reset to first page when favorites filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [showFavoritesOnly]);

  // Get datasets from server response (server handles favorites filtering)
  const filteredDatasets = useMemo(() => {
    return data?.results || [];
  }, [data?.results]);

  const handlePageChange = (
    _event: React.ChangeEvent<unknown>,
    value: number,
  ) => {
    setCurrentPage(value);
  };
  return (
    <>
      {(isDatasetFolderLoading || isDatasetFolderFetching) && (
        <Grid container spacing={isMobile ? 1 : 2} columns={12}>
          {[...Array(isMobile ? 3 : 6)].map((_, index) => (
            <Grid item xs={12} sm={12} md={12} lg={6} xl={4} key={index}>
              <Skeleton
                variant="rounded"
                height={isMobile ? 120 : 200}
                animation="wave"
              />
            </Grid>
          ))}
        </Grid>
      )}
      {isError && (
        <Alert severity="error">
          <AlertTitle>Error loading datasets</AlertTitle>
          {extractErrorDetail(error, "An error occurred")}
        </Alert>
      )}
      <Grid
        container
        spacing={isMobile ? 1 : 0.5}
        columns={12}
        sx={{ mb: isMobile ? 1 : 2, mt: isMobile ? 0.5 : 1 }}
        data-testid="ds-folder-grid"
      >
        {isDatasetFolderDataSuccess &&
          !isDatasetFolderLoading &&
          !isDatasetFolderFetching &&
          datasetFolderData?.results?.length > 0 &&
          !atomResults &&
          !folder_id &&
          datasetFolderData?.results?.map(
            (datasetFolder: any, index: number) => (
              <Grid
                item
                xs={12}
                sm={6}
                md={4}
                lg={3}
                xl={2.4}
                key={datasetFolder.id}
                data-testid={`ds-folder-item-${index}`}
              >
                <DatasetCard data={datasetFolder} folderId={datasetFolder.id} />
              </Grid>
            ),
          )}
      </Grid>

      <Divider
        sx={{
          borderStyle: "none",
        }}
        hidden={
          folder_id ||
          !datasetFolderData?.results?.length ||
          (isLoading && isDatasetFolderLoading) ||
          (isFetching && isDatasetFolderFetching) ||
          atomResults
        }
      />
      {isLoading && (
        <Grid container spacing={isMobile ? 1 : 2} columns={12}>
          {[...Array(isMobile ? 3 : 6)].map((_, index) => (
            <Grid item xs={12} sm={12} md={12} lg={6} xl={4} key={index}>
              <Skeleton
                variant="rounded"
                height={isMobile ? 120 : 200}
                animation="wave"
              />
            </Grid>
          ))}
        </Grid>
      )}
      <Grid
        container
        spacing={isMobile ? 1 : 2}
        columns={12}
        sx={{ mt: 0 }}
        data-testid="ds-dataset-grid"
      >
        {isSuccess &&
          !isLoading &&
          filteredDatasets.length > 0 &&
          filteredDatasets.map((dataset: any, index: number) => (
            <Grid
              item
              xs={12}
              md={12}
              lg={12}
              xl={6}
              key={dataset.id}
              data-testid={`ds-dataset-item-${index}`}
            >
              <DatasetCard
                data={dataset}
                folderId={folder_id}
                atomResults={atomResults}
              />
            </Grid>
          ))}
      </Grid>

      {/* Pagination Controls */}
      {isSuccess && filteredDatasets.length > 0 && data?.total_pages > 1 && (
        <Box
          display="flex"
          flexDirection={isMobile ? "column" : "row"}
          justifyContent="center"
          alignItems="center"
          mt={isMobile ? 2 : 3}
          gap={isMobile ? 1 : 2}
        >
          <Pagination
            count={data.total_pages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
            size={isMobile ? "small" : "medium"}
            showFirstButton={!isMobile}
            showLastButton={!isMobile}
            data-testid="ds-pagination"
            data-attr="datasets:btn:pagination"
          />
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: isMobile ? "0.75rem" : undefined }}
          >
            {isMobile
              ? `${(currentPage - 1) * pageSize + 1}-${Math.min(
                  currentPage * pageSize,
                  data.total_items,
                )} of ${data.total_items}`
              : `Showing ${(currentPage - 1) * pageSize + 1}-${Math.min(
                  currentPage * pageSize,
                  data.total_items,
                )} of ${data.total_items} datasets`}
          </Typography>
        </Box>
      )}

      {isSuccess && filteredDatasets.length === 0 && (
        <Box
          mt={3}
          display="flex"
          justifyContent="center"
          alignItems="center"
          flexDirection="column"
          data-testid="ds-empty-state"
        >
          <NoDataOverlay
            message={
              showFavoritesOnly
                ? "No favorite datasets found"
                : "No datasets found"
            }
          />
          {!showFavoritesOnly && (
            <Box mt={2}>
              <Can
                perform="dataset:write"
                yes={() => (
                  <CreateItemButton
                    itemName="Create dataset"
                    createItemModal={CreateDatasetModal}
                    data-testid="ds-btn-create-empty"
                    data-attr="datasets:btn:create-dataset-empty-state"
                  />
                )}
              />
            </Box>
          )}
        </Box>
      )}
    </>
  );
};

export default DatasetCardsGrid;
